import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MdExpandMore, MdExpandLess } from "react-icons/md";

// ── All FAQs organised by category ──────────────────────────────────────────
export const faqCategories = [
  {
    category: "Orders & Products",
    items: [
      {
        question: "How do I place an order?",
        answer:
          "Simply browse through our website and explore our full collection of premium products. Once you find an item you love, click on it to view details and proceed to place your order seamlessly through our secure checkout process.",
      },
      {
        question: "How can I view product details?",
        answer:
          "Click on any product you are interested in, and all relevant details such as description, price, and specifications will be displayed clearly on the product page.",
      },
      {
        question: "Are the products original and brand new?",
        answer:
          "Yes. All our products are 100% original and brand new. We source directly from trusted international suppliers and verified brands across China, the UK, Turkey, and other reputable markets to ensure quality and authenticity.",
      },
      {
        question: "How do I know if a product is available?",
        answer:
          "Each product displays real-time availability. A green indicator or stock label shows whether the item is available or limited in stock.",
      },
      {
        question: "Can I order multiple items at once?",
        answer:
          "Yes, you can order multiple items in a single purchase. Simply adjust the quantity for each product before adding it to your cart.",
      },
      {
        question: "Do product prices change?",
        answer:
          "Yes, prices may be adjusted based on market conditions, exchange rates, and supplier pricing. However, we always ensure fair and transparent pricing for our customers.",
      },
      {
        question: "Can I cancel an order after placing it?",
        answer:
          "Yes, you can cancel your order as long as it has not yet been processed or shipped. Kindly contact our support team as quickly as possible for assistance.",
      },
      {
        question: "What happens after I place an order?",
        answer:
          "Once your order is placed, it is immediately processed by our team. You will receive updates on your order status, including confirmation and delivery progress.",
      },
      {
        question: "Will I receive an order confirmation email?",
        answer:
          "Yes, a confirmation email will be sent to you immediately after your order is successfully placed, serving as proof of purchase and order details.",
      },
      {
        question: "Can I reorder a previous purchase?",
        answer:
          "Yes, if the product is still available, you can easily reorder it directly from your account or by revisiting the product page on our website.",
      },
    ],
  },
  {
    category: "Payments",
    items: [
      {
        question: "What payment methods do you accept?",
        answer:
          "We accept multiple secure payment methods including bank transfers, debit/credit cards, USSD, mobile banking, and eNaira. All payments are processed through Flutterwave, a trusted and globally recognised payment gateway. During checkout, all available payment options will be displayed for your convenience.",
      },
      {
        question: "Is online payment secure?",
        answer:
          "Yes. All payments are processed securely through Flutterwave, which uses advanced encryption and fraud protection systems to ensure your financial information remains safe at all times.",
      },
      {
        question: "Can I pay on delivery?",
        answer:
          "No, we currently do not offer pay-on-delivery. This ensures faster processing, order security, and helps us deliver efficiently without delays or risk of cancellations after dispatch.",
      },
      {
        question: "Why was my payment declined?",
        answer:
          "Payments may be declined due to insufficient funds, incorrect card or bank details, network issues, or bank restrictions. If this occurs, please contact your bank or reach out to our support team immediately for assistance.",
      },
      {
        question: "Will I receive a payment receipt?",
        answer:
          "Yes, every successful payment generates an automated receipt sent to your email. This serves as your official proof of payment.",
      },
      {
        question: "Can I pay using bank transfer?",
        answer:
          "Yes, bank transfer is fully supported and available among our listed payment options at checkout.",
      },
      {
        question: "Are there hidden charges?",
        answer:
          "No. We operate a fully transparent pricing system. There are no hidden charges — customers only pay the exact product price plus clearly stated fees where applicable.",
      },
      {
        question: "How long does payment verification take?",
        answer:
          "Payment verification is usually instant or completed within a few minutes. Our system and Flutterwave's fast processing ensure quick confirmation so your order can be processed immediately.",
      },
    ],
  },
  {
    category: "Delivery & Shipping",
    items: [
      {
        question: "How long does delivery take?",
        answer:
          "Within Lagos, delivery typically takes 2–3 hours after order confirmation. For other locations, delivery may take 1–3 working days depending on the destination.",
      },
      {
        question: "Do you deliver nationwide?",
        answer: "Yes, we deliver across all states in Nigeria.",
      },
      {
        question: "How much is the delivery fee?",
        answer:
          "Delivery fees are determined based on location and package size. Customers will be informed of the exact delivery cost before dispatch so there are no surprises.",
      },
      {
        question: "Can I track my order?",
        answer:
          "Yes, once your order is confirmed, you can track it through your account dashboard under 'Orders' or contact our support team via email at support@vethinteriors.com or WhatsApp on our Contact page.",
      },
      {
        question: "What happens if I miss my delivery?",
        answer:
          "If delivery is missed, the item will be returned to our facility. You can then contact us to reschedule delivery or arrange pickup.",
      },
      {
        question: "Do you offer same-day delivery?",
        answer:
          "Yes, same-day delivery is available within Lagos. Once your order is confirmed, processing begins immediately to ensure fast dispatch. For other states, delivery timelines may vary depending on location.",
      },
      {
        question: "Can I change my delivery address after ordering?",
        answer:
          "Yes, but only if the order has not yet been shipped. Please contact our support team immediately to request any changes.",
      },
      {
        question: "Which courier service do you use?",
        answer:
          "We work with trusted logistics partners depending on the nature and destination of each order. All deliveries are handled with care to ensure safe and timely arrival.",
      },
      {
        question: "Why does delivery time sometimes vary?",
        answer:
          "Delivery may vary due to location distance, courier availability, or weather conditions. However, we always prioritise fast dispatch.",
      },
      {
        question: "Why is the delivery fee not fixed?",
        answer:
          "Delivery fees vary depending on your location, item size, and courier service. You will always be informed before dispatch.",
      },
      {
        question: "Do you deliver to remote areas?",
        answer:
          "Yes, but delivery time may vary depending on accessibility and logistics coverage.",
      },
    ],
  },
  {
    category: "Returns & Refunds",
    items: [
      {
        question: "What is your return policy?",
        answer:
          "We only accept returns for damaged, defective, or incorrect items. Products must be reported within the specified return window and must remain unused and in original condition. Certain luxury or fragile items may not be eligible for return due to handling sensitivity.",
      },
      {
        question: "How do I return a product?",
        answer:
          "To initiate a return, contact our support team immediately with your order details. Items must be unused and in original condition for eligibility.",
      },
      {
        question: "Can I return a damaged item?",
        answer:
          "Yes. If your item arrives damaged, you must report it within 3 hours of delivery before usage. Kindly contact our support team or WhatsApp for immediate resolution.",
      },
      {
        question: "How long do refunds take?",
        answer:
          "Refunds are typically processed within 4–5 working days after the returned item has been reviewed and approved.",
      },
      {
        question: "Are there products that cannot be returned?",
        answer:
          "Yes. Certain luxury, fragile, and custom-made items are non-returnable due to their sensitive nature and handling requirements.",
      },
      {
        question: "Will delivery charges be refunded?",
        answer:
          "No. Delivery fees are non-refundable as they cover logistics and service costs, which are completed once dispatch occurs.",
      },
      {
        question: "What should I do if I receive the wrong item?",
        answer:
          "If you receive an incorrect item, please contact our support team immediately. Do not open or use the package. We will arrange a quick resolution, including replacement or correction.",
      },
    ],
  },
  {
    category: "Account & Registration",
    items: [
      {
        question: "Do I need an account to place an order?",
        answer:
          "Yes, creating an account allows you to track orders, view purchase history, and enjoy a faster checkout experience.",
      },
      {
        question: "How do I create an account?",
        answer:
          "Simply click on 'Login' then select 'Create Account', fill in your name, email and password, and you are all set. It takes less than a minute.",
      },
      {
        question: "Can I change my account details?",
        answer:
          "Yes, you can update your profile information anytime from your account dashboard.",
      },
      {
        question: "Can I modify my order after placing it?",
        answer:
          "Yes, but only before your order has been processed or shipped. Once dispatch begins, changes cannot be made.",
      },
      {
        question: "How fast is my order processed?",
        answer:
          "Orders are processed immediately after payment confirmation to ensure fast delivery, especially within Lagos.",
      },
    ],
  },
  {
    category: "Trust & Security",
    items: [
      {
        question: "How do I know this is a real business?",
        answer:
          "We are a registered and trusted interior brand with verified suppliers and secure payment systems powered by Flutterwave. We have been in business for over 15 years and serve customers across all of Nigeria.",
      },
      {
        question: "Is my payment safe?",
        answer:
          "Yes. All transactions are encrypted and processed through Flutterwave, one of Africa's most secure payment gateways.",
      },
    ],
  },
  {
    category: "Customer Support",
    items: [
      {
        question: "How can I contact support?",
        answer:
          "You can reach us via:\n• Email: support@vethinteriors.com\n• WhatsApp / Phone: 08106079897\n• Contact form on our website",
      },
      {
        question: "What are your support hours?",
        answer:
          "We respond within business hours (Monday – Saturday, 9 AM – 6 PM) and ensure fast resolution of all customer requests.",
      },
    ],
  },
];

// Flatten for home page preview
export const faqs = faqCategories.flatMap((c) => c.items);

// ── Accordion item ────────────────────────────────────────────────────────────
function FAQItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-200 ${
        open ? "border-amber-400" : "border-black/10 dark:border-white/10"
      }`}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white dark:bg-black/40 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <span className="flex items-center gap-3">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-800/60 text-amber-600 dark:text-amber-300 flex items-center justify-center text-xs font-bold">
            {index + 1}
          </span>
          <span className="font-semibold text-black/80 dark:text-white/90 text-sm sm:text-base text-left">
            {question}
          </span>
        </span>
        {open ? (
          <MdExpandLess className="text-amber-500 text-xl flex-shrink-0" />
        ) : (
          <MdExpandMore className="text-amber-500 text-xl flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-5 pb-5 pt-2 bg-white dark:bg-black/30 text-black/60 dark:text-white/70 text-sm leading-relaxed">
          {answer.split("\n").map((line, i) => {
            const trimmed = line.trim();
            if (!trimmed) return null;
            if (/^[•\-]/.test(trimmed)) {
              return (
                <div key={i} className="flex items-start gap-2 mt-1">
                  <span className="text-amber-500 flex-shrink-0">•</span>
                  <span>{trimmed.replace(/^[•\-]\s*/, "")}</span>
                </div>
              );
            }
            return <p key={i} className="mt-1">{trimmed}</p>;
          })}
        </div>
      )}
    </div>
  );
}

// ── FAQ Section (home page — first 6 questions) ───────────────────────────────
export function FAQSection() {
  const preview = faqs.slice(0, 6);

  return (
    <div className="w-full py-14 px-4 bg-black/5 dark:bg-white/5">
      <div className=" mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-black/90 dark:text-white mb-2">
            Frequently Asked Questions
          </h2>
          <p className="text-black/50 dark:text-white/50 text-sm">
            Everything you need to know about shopping with VETH INTERIORS
          </p>
        </div>

        <div className="space-y-3">
          {preview.map((faq, i) => (
            <FAQItem key={i} index={i} question={faq.question} answer={faq.answer} />
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/faq"
            onClick={() => window.scrollTo(0, 0)}
            className="inline-block px-10 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-full shadow hover:from-amber-700 hover:to-yellow-700 transition-all duration-300"
          >
            View All FAQs
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── Full FAQ Page ─────────────────────────────────────────────────────────────
const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const categoryNames = ["All", ...faqCategories.map((c) => c.category)];

  const displayItems =
    activeCategory === "All"
      ? faqCategories.flatMap((c) => c.items)
      : faqCategories.find((c) => c.category === activeCategory)?.items || [];

  return (
    <div className="bg-white dark:bg-black min-h-screen flex flex-col items-center">
      <div className="w-full pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-bold text-black/90 dark:text-white mb-3">
              Frequently Asked Questions
            </h1>
            <p className="text-black/50 dark:text-white/50 text-sm">
              Got a question? We've got answers. Browse by category below.
            </p>
          </div>

          {/* Category filter tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {categoryNames.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                  activeCategory === cat
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white dark:bg-white/5 text-black/60 dark:text-white/60 border-black/10 dark:border-white/10 hover:border-amber-400 hover:text-amber-600 dark:hover:text-amber-400"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* FAQ list */}
          <div className="space-y-3">
            {displayItems.map((faq, i) => (
              <FAQItem key={i} index={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-14 bg-black/5 dark:bg-white/5 rounded-2xl p-8 text-center border border-black/10 dark:border-white/10">
            <h3 className="text-xl font-bold text-black/90 dark:text-white mb-2">
              Still have questions?
            </h3>
            <p className="text-black/50 dark:text-white/50 text-sm mb-6">
              Our team is ready to help you.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://wa.me/2348106079897"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-full transition-all"
              >
                WhatsApp Us
              </a>
              <a
                href="mailto:support@vethinteriors.com"
                className="px-8 py-3 border-2 border-amber-500 text-amber-600 dark:text-amber-400 font-bold rounded-full hover:bg-amber-500 hover:text-white transition-all"
              >
                Email Support
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FAQPage;
